import React, {FC, useRef, useState, useEffect} from "react";
import { Editor as TinyEditor } from "@tinymce/tinymce-react";
import Error from "../Error";
import "./editor.scss";
import AppLoader from "../AppLoader";

export interface ReportMacroItem {
    shortname: string;
    expandedText: string;
}

interface EditorProps {
    value: string;
    onChangeHandler: (content: string) => void;
    error?: string;
    readOnly?: boolean;
    editorInstanceRef: React.MutableRefObject<any>;
    /** Called when user presses Ctrl+S / Cmd+S inside the editor (needed when editor is in iframe e.g. drawer) */
    onSaveShortcut?: () => void;
    /** Autocomplete macros: type #shortname to expand */
    macros?: ReportMacroItem[];
    /** Callback when user wants to add a new macro from editor toolbar */
    onAddMacro?: () => void;
    /** When true, editor fills available height (for fullscreen layout) */
    fullscreenHeight?: boolean;
    /** Override height (e.g. "50vh" for mobile when parent height chain is unreliable) */
    height?: string;
    /** Match radiolinq viewer dark UI (report panel beside DICOM viewer) */
    viewerDarkMode?: boolean;
}

const Editor: FC<EditorProps> = ({
                                     value,
                                     onChangeHandler,
                                     error,
                                     readOnly = false,
                                     editorInstanceRef,
                                     onSaveShortcut,
                                     macros = [],
                                     onAddMacro,
                                     fullscreenHeight = false,
                                     height: heightProp,
                                     viewerDarkMode = false,
                                 }) => {
    const [editorLoading, setEditorLoading] = useState(true);
    const macrosRef = useRef(macros);
    useEffect(() => {
        macrosRef.current = macros;
    }, [macros]);

    const cleanWordHtml = (root: HTMLElement) => {
        // Remove Word classes
        root.querySelectorAll("[class]").forEach(el => {
            if (/Mso|WordSection/i.test(el.className)) {
                el.removeAttribute("class");
            }
        });

        // Remove inline margins & line heights
        root.querySelectorAll("*").forEach(el => {
            const style = (el as HTMLElement).style;

            if (!style) return;

            style.margin = "";
            style.marginTop = "";
            style.marginBottom = "";
            style.lineHeight = "";
        });

        // Normalize fonts (prevent Word font override)
        // root.querySelectorAll("span").forEach(span => {
        //     const style = span.getAttribute("style") || "";
        //
        //     if (/font-family/i.test(style)) {
        //         span.style.fontFamily = "inherit";
        //     }
        // });
    };

    const convertWordFakeLists = (root: HTMLElement) => {
        const paragraphs = Array.from(root.querySelectorAll("p"));
        let currentList: HTMLUListElement | HTMLOListElement | null = null;

        const BULLET_REGEX = /^[\s\u00A0]*[•·▪◦■\-o][\s\u00A0]*/;
        const NUMBER_REGEX = /^[\s\u00A0]*\d+\.[\s\u00A0]*/;

        paragraphs.forEach(p => {
            const text = p.textContent || "";

            const isBullet = BULLET_REGEX.test(text);
            const isNumber = NUMBER_REGEX.test(text);

            if (!isBullet && !isNumber) {
                currentList = null;
                return;
            }

            if (
                !currentList ||
                (isBullet && currentList.tagName !== "UL") ||
                (isNumber && currentList.tagName !== "OL")
            ) {
                currentList = document.createElement(isBullet ? "ul" : "ol");
                p.parentNode?.insertBefore(currentList, p);
            }

            const li = document.createElement("li");
            li.innerHTML = p.innerHTML;

            removeLeadingWordBullet(li, isNumber ? NUMBER_REGEX : BULLET_REGEX);

            if (li.innerHTML.trim() !== "") {
                currentList.appendChild(li);
            }

            p.remove();
        });
    };

    const removeLeadingWordBullet = (
        li: HTMLElement,
        regex: RegExp
    ) => {
        const childNodes = Array.from(li.childNodes);

        for (const node of childNodes) {
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.nodeValue || "";

                if (regex.test(text)) {
                    node.nodeValue = text.replace(regex, "");
                    return;
                }
            }

            if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                const text = el.textContent || "";

                const style = el.getAttribute("style") || "";

                const bulletOnly =
                    regex.test(text) &&
                    text.replace(regex, "").trim() === "";

                const symbolFont =
                    /symbol|wingdings|webdings/i.test(style);

                if (bulletOnly || symbolFont) {
                    el.remove();
                    continue;
                }

                if (regex.test(text)) {
                    el.textContent = text.replace(regex, "");
                    return;
                }
            }
        }
    };


    return (
        <div className={`editor${viewerDarkMode ? " editor--viewer-dark" : ""}`}>
            {editorLoading && (
                <div className="editor-loader">
                    <AppLoader loading />
                </div>
            )}
            <TinyEditor
                tinymceScriptSrc="/tinymce/tinymce.min.js"
                licenseKey="gpl"
                value={value}
                onInit={(_, editor) => {
                    editorInstanceRef.current = editor;
                    setEditorLoading(false);
                    // Explicitly enable browser spell check on editor body (browser_spellcheck alone can be unreliable)
                    const body = editor.getBody();
                    if (body) {
                        body.setAttribute("spellcheck", "true");
                        (body as HTMLElement).spellcheck = true;
                    }
                    if (onSaveShortcut) {
                        editor.addShortcut("ctrl+s", "Save draft", () => onSaveShortcut());
                        editor.addShortcut("meta+s", "Save draft", () => onSaveShortcut());
                    }
                    // Keep native select-all stable inside iframe editor
                    editor.addShortcut("ctrl+a", "Select all", () => {
                        editor.execCommand("SelectAll");
                    });
                    editor.addShortcut("meta+a", "Select all", () => {
                        editor.execCommand("SelectAll");
                    });
                    editor.on("PastePostProcess", (e) => {
                        const node = e.node as HTMLElement;
                        if (!node) return;

                        // Pasted images: default to 40% width so they're resizable (inline style).
                        // Skip images that already have a meaningful width (incl. viewer screenshots).
                        node.querySelectorAll("img").forEach((img) => {
                            const el = img as HTMLImageElement;
                            if (!el.getAttribute("data-vimg")) {
                                el.style.width = "40%";
                                el.style.height = "auto";
                            }
                        });

                        // Paste pipeline may add images after this; run again after a short delay.
                        // Only touch images that still have no width style set.
                        setTimeout(() => {
                            const body = editor.getBody();
                            if (!body) return;
                            body.querySelectorAll("img").forEach((img) => {
                                const el = img as HTMLImageElement;
                                // Never override viewer screenshots.
                                if (el.getAttribute("data-vimg")) return;
                                const styleWidth = (el.getAttribute("style") || "").match(/\bwidth\s*:\s*([^;]+)/i);
                                const styleWidthVal = styleWidth ? styleWidth[1].trim() : "";
                                const hasAnyWidth = styleWidthVal && styleWidthVal !== "auto";
                                if (!hasAnyWidth) {
                                    el.style.width = "40%";
                                    el.style.height = "auto";
                                    el.removeAttribute("width");
                                    el.removeAttribute("height");
                                }
                            });
                        }, 150);

                        const html = node.innerHTML;

                        const isWord =
                            /mso-|urn:schemas-microsoft-com:office|class="?Mso/i.test(html);

                        if (!isWord) return;

                        // 1️⃣ Clean Word junk
                        cleanWordHtml(node);

                        // 2️⃣ Convert fake lists
                        convertWordFakeLists(node);
                    });


                    const getSuggestions = (pattern: string, maxResults: number) => {
                        const currentMacros = macrosRef.current;
                        return currentMacros
                            .filter(m => m.shortname?.toLowerCase().includes(pattern.toLowerCase()))
                            .slice(0, maxResults)
                            .map(m => ({
                                value: m.expandedText || m.shortname || '',
                                text: `${m.shortname} → ${(m.expandedText || '').substring(0, 50)}${(m.expandedText || '').length > 50 ? '...' : ''}`,
                            }));
                    };
                    const onMacroAction = (autocompleteApi: any, rng: any, expandedValue: string) => {
                        const ed = editorInstanceRef.current;
                        if (ed) {
                            ed.selection.setRng(rng);
                            ed.insertContent(expandedValue);
                            autocompleteApi.hide();
                        }
                    };

                    // Autocompleter: type #shortname to expand
                    editor.ui.registry.addAutocompleter('report-macros-autocomplete', {
                        trigger: '#',
                        minChars: 1,
                        columns: 1,
                        fetch: (pattern, maxResults) => Promise.resolve(getSuggestions(pattern, maxResults)),
                        onAction: onMacroAction,
                    });
                }}
                onEditorChange={(content) => {
                    // NORMAL change flow — cursor safe
                    onChangeHandler(content);
                }}
                init={{
                    height: heightProp ?? (fullscreenHeight ? '100%' : '620px'),
                    ...(viewerDarkMode
                        ? {
                              skin: "oxide-dark",
                              content_css: "dark",
                          }
                        : {}),
                    browser_spellcheck: true,
                    contextmenu: false, // Let browser show native context menu (spell check suggestions) on right-click
                    // menubar: "file edit view insert format table",
                    // menu: {
                    //     view: {title: "View", items: "fullscreen"}
                    // },
                    menubar: false,
                    branding: false,
                    promotion: false,
                    toolbar_sticky: true,
                    toolbar_sticky_offset: 0,
                    mobile : {
                      toolbar_sticky: true,
                    },
                    plugins: ["fullscreen", "advlist","lists","table","image","pagebreak", "link", "powerpaste"],
                    toolbar: onAddMacro
                        ? 'undo redo | blocks fontfamily fontsize bold italic underline strikethrough | checklist numlist bullist align lineheight fullscreen | indent outdent | link media table mergetags browsemacros addmacro | addcomment showcomments | spellcheckdialog a11ycheck typography uploadcare | emoticons charmap | removeformat | image'
                        : 'undo redo | blocks fontfamily fontsize bold italic underline strikethrough | checklist numlist bullist align lineheight fullscreen | indent outdent | link media table mergetags browsemacros | addcomment showcomments | spellcheckdialog a11ycheck typography uploadcare | emoticons charmap | removeformat | image',
                    file_picker_types: "image",
                    file_picker_callback: (callback) => {
                        const input = document.createElement("input");
                        input.type = "file";
                        input.accept = "image/*";
                        input.onchange = () => {
                            // @ts-ignore
                            const file = input.files[0];
                            const reader = new FileReader();
                            reader.onload = () => {
                                callback(reader.result as string, { title: file.name });
                            };
                            reader.readAsDataURL(file);
                        };
                        input.click();
                    },

                    paste_as_text: false,
                    paste_enable_default_filters: true,
                    paste_merge_formats: true,
                    paste_remove_styles_if_webkit: false,

                    paste_remove_styles: false,
                    paste_remove_spans: false,

                    powerpaste_word_import: "merge",
                    powerpaste_html_import: "merge",

                    valid_styles: {
                        "*": `
                        font-family,
                        font-size,
                        font-weight,
                        font-style,
                        text-decoration,
                        text-align,
                        line-height,
                        color,
                        background-color
                      `,
                        img: "width height min-width max-width",
                    },

                    paste_preprocess: (plugin, args) => {
                        let html = args.content;

                        // Remove Word junk but keep styles
                        html = html.replace(/<!--[\s\S]*?-->/gi, "");
                        html = html.replace(/<\\?\?xml[^>]*>/gi, "");
                        html = html.replace(/<\/?o:[^>]*>/gi, "");

                        // Pasted images: default to 40% width (resizable via inline style)
                        html = html.replace(/<img(\s[^>]*)?>/gi, (match, rest) => {
                            rest = rest || "";
                            const hasStyle = /\bstyle\s*=/i.test(rest);
                            const widthMatch = rest.match(/\bstyle\s*=\s*["']([^"']*)["']/i);
                            let style = widthMatch ? widthMatch[1] : "";
                            style = style.replace(/\bwidth\s*:[^;]+;?/gi, "").replace(/\bheight\s*:[^;]+;?/gi, "").trim();
                            const newStyle = style ? `width:40%;height:auto;${style}` : "width:40%;height:auto;";
                            if (hasStyle) {
                                rest = rest.replace(/\bstyle\s*=\s*["'][^"']*["']/i, `style="${newStyle}"`);
                            } else {
                                rest = rest.trim() ? `${rest.trim()} style="${newStyle}"` : ` style="${newStyle}"`;
                            }
                            return `<img${rest}>`;
                        });

                        args.content = html;
                    },


                    forced_root_block: "p",
                    lists_indent_on_tab: true,

                    resize: true,
                    image_dimensions: true,
                    object_resizing: "img",

                    font_family_formats:
                        "Montserrat=Montserrat,Arial,sans-serif;" +
                        "Andale Mono=andale mono,times;" +
                        "Arial=arial,helvetica,sans-serif;" +
                        "Arial Black=arial black,avant garde;" +
                        "Book Antiqua=book antiqua,palatino;" +
                        "Cambria=cambria,serif;" +
                        "Comic Sans MS=comic sans ms,sans-serif;" +
                        "Courier New=courier new,courier;" +
                        "Georgia=georgia,palatino;" +
                        "Helvetica=helvetica;" +
                        "Impact=impact,chicago;" +
                        "Symbol=symbol;" +
                        "Tahoma=tahoma,arial,helvetica,sans-serif;" +
                        "Terminal=terminal,monaco;" +
                        "Times New Roman=times new roman,times;" +
                        "Trebuchet MS=trebuchet ms,geneva;" +
                        "Verdana=verdana,geneva;",

                    content_style: `
                        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');
                        @import url('https://fonts.googleapis.com/css2?family=Calibri&family=Cambria&display=swap');

                        body {
                          font-family: inherit;
                          font-size: 14px;
                          line-height: 1.4;
                        }
                    
                        p {
                          margin: 0 0 8px 0;
                          line-height: 1.4;
                        }
                    
                        ul, ol {
                          margin: 0 0 8px 0;
                          padding-left: 24px;
                          line-height: inherit;
                        }
                    
                        li {
                          margin: 0;
                          line-height: inherit;
                        }
                        
                        img {
                            max-width: 100%;
                            display: block;
                        }
                        ${
                            viewerDarkMode
                                ? `
                        body, body:focus, body:focus-visible {
                          outline: none !important;
                          box-shadow: none !important;
                        }
                        `
                                : ""
                        }
                    `,

                    setup: (editor) => {
                        if (onAddMacro) {
                            editor.ui.registry.addButton('addmacro', {
                                icon: 'template',
                                tooltip: 'Add autocomplete shortcut',
                                onAction: () => onAddMacro(),
                            });
                        }
                        editor.ui.registry.addButton('browsemacros', {
                            icon: 'unordered-list',
                            tooltip: 'Browse macros',
                            onAction: () => {
                                const currentMacros = macrosRef.current;
                                if (!currentMacros.length) {
                                    editor.windowManager.alert('No macros available. Add macros from the Report Macros page or use the Add shortcut button.');
                                    return;
                                }
                                const listItems = currentMacros.map(m => ({
                                    text: `${m.shortname} → ${(m.expandedText || '').substring(0, 60)}${(m.expandedText || '').length > 60 ? '...' : ''}`,
                                    value: m.expandedText || m.shortname || '',
                                }));
                                editor.windowManager.open({
                                    title: 'Insert Macro',
                                    size: 'medium',
                                    body: {
                                        type: 'panel',
                                        items: [
                                            {
                                                type: 'listbox',
                                                name: 'macro',
                                                label: 'Select a macro to insert',
                                                items: listItems,
                                            },
                                        ],
                                    },
                                    buttons: [
                                        { type: 'cancel', text: 'Cancel' },
                                        { type: 'submit', text: 'Insert', name: 'insert' },
                                    ],
                                    onSubmit: (api) => {
                                        const data = api.getData();
                                        if (data.macro) {
                                            editor.insertContent(data.macro);
                                        }
                                        api.close();
                                    },
                                });
                            },
                        });
                        const insertImageAt40 = (imageData: string) => {
                            editor.insertContent(
                                `<p><img src="${imageData}" style="width:40%;height:auto;" /></p>`
                            );
                        };

                        const handlePaste = (e: Event) => {
                            const clip = (e as ClipboardEvent).clipboardData;
                            if (!clip || !clip.items.length) return;
                            for (let i = 0; i < clip.items.length; i++) {
                                const item = clip.items[i];
                                if (item.type.indexOf("image") !== -1) {
                                    const blob = item.getAsFile();
                                    if (!blob) return;
                                    e.preventDefault();
                                    e.stopImmediatePropagation();
                                    const reader = new FileReader();
                                    reader.onload = () =>
                                        insertImageAt40(reader.result as string);
                                    reader.readAsDataURL(blob);
                                    return;
                                }
                            }
                        };

                        // TinyMCE paste event
                        editor.on("paste", handlePaste);
                        // Native paste on iframe document (catches paste before TinyMCE default)
                        editor.on("init", () => {
                            const doc = editor.getDoc();
                            if (doc) doc.addEventListener("paste", handlePaste, true);
                        });
                    },
                }}
            />

            <Error message={error ?? ""} />
        </div>
    );
};

export default Editor;

